import React from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import { formatBytes } from "aldoo-ra/Admin/KPI/format"
import { ListQuotas } from "./api"
import { Owner, formatDate } from "./ui"

/**
 * Per-user transfer quota usage (rolling 30-day window matches the server
 * enforcement in `quota.js`). Shows the plan, monthly allowance, used so far,
 * a usage bar, and lifetime totals.
 */

function UsageBar({ pct }) {
  if (pct == null) {
    return <span className="text-xs text-admin_text_secondary">unlimited</span>
  }
  const clamped = Math.max(0, Math.min(100, pct))
  const color = clamped >= 90 ? "bg-red-500" : clamped >= 70 ? "bg-amber-500" : "bg-emerald-500"
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-xs font-medium tabular-nums w-10 text-right">{clamped.toFixed(0)}%</span>
    </div>
  )
}

const QuotasTab = () => {
  const columns = [
    {
      id: "email",
      header: "User",
      sortable: true,
      render: (_, item) => <Owner owner={{ email: item.email, name: item.name }} />,
    },
    {
      id: "plan",
      header: "Plan",
      render: (_, item) => (
        <div className="flex flex-col leading-tight">
          <span>{item.planName}</span>
          {item.pid && <span className="text-xs text-admin_text_secondary">{item.pid}</span>}
        </div>
      ),
    },
    {
      id: "quota",
      header: "Monthly quota",
      render: (_, item) => (
        <span className="whitespace-nowrap">
          {item.quotaBytes == null ? "Unlimited" : formatBytes(item.quotaBytes)}
        </span>
      ),
    },
    {
      id: "used",
      header: "Used (30d)",
      sortable: true,
      render: (_, item) => (
        <span className="whitespace-nowrap">
          {formatBytes(item.usedBytes)}
          <span className="text-xs text-admin_text_secondary ml-1">· {item.usedCount} xfer</span>
        </span>
      ),
    },
    {
      id: "pct",
      header: "% used",
      render: (_, item) => <UsageBar pct={item.usedPct} />,
    },
    {
      id: "lifetime",
      header: "Lifetime",
      sortable: true,
      render: (_, item) => (
        <span className="whitespace-nowrap">
          {formatBytes(item.lifetimeBytes)}
          <span className="text-xs text-admin_text_secondary ml-1">· {item.lifetimeCount} xfer</span>
        </span>
      ),
    },
    {
      id: "lastUpload",
      header: "Last upload",
      render: (_, item) => (
        <span className="whitespace-nowrap">{formatDate(item.lastUploadAt)}</span>
      ),
    },
  ]

  return (
    <DataTable
      variant="admin_browser"
      apiMethod={ListQuotas}
      searchField="q"
      searchPlaceholder="Search user email or name…"
      sort={{ used: -1 }}
      localUserPreferencesKey="admin.transferBrowser.quotas"
      renders={columns}
      preferredHeight="60vh"
      itemHeight="h-14"
      noData={<Typography variant="admin_body">No quota records</Typography>}
    />
  )
}

export default QuotasTab
